import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { changePageInfo } from '../../../../../state/store/reducers/ui/headerSlice';
import { Button, Pagination } from '../../../../../components/common';
import { ListState } from '../../../../../components/blocks';
import useToast from '../../../../../state/hooks/useToast';
import useAlert from '../../../../../state/hooks/useAlert';
import plansService from '../../../../../services/api/smartbill/plansService';
import schoolService from '../../../../../services/api/unified-api/schoolService';
import ModalCreatePlan from './ModalCreatePlan';
import ModalAddToPlan from './ModalAddToPlan';
import { Input } from '../../../../../components/form';
import CustomCheckbox from '../../../../../components/common/CustomCheckBox';


function PlanDetailsPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { planId } = useParams();
    const { addToast } = useToast();
    const { openAlertConfirm } = useAlert();

    const [editPlanModal, setEditPlanModal] = useState({ open: false });
    const [addToPlanModal, setAddToPlanModal] = useState({ open: false });

    useEffect(() => {
        dispatch(changePageInfo({ title: "Plan Details" }));
    }, []);

    const planQuery = useQuery({
        queryKey: ['plans', planId],
        queryFn: () => plansService.getPlan(planId!),
        enabled: !!planId,
    });

    const planFeaturesQuery = useQuery({
        queryKey: ['plan-features', planId],
        queryFn: () => plansService.getPlanFeatures(planId!),
        enabled: !!planId,
    });

    const planSchoolsQuery = useQuery({
        queryKey: ['plan-schools', planId],
        queryFn: () => plansService.getPlanSchools(planId!),
        enabled: !!planId,
    });

    // Query to fetch all schools for name lookup
    const schoolsQuery = useQuery({
        queryKey: ['schools'],
        queryFn: () => schoolService.getSchools({}), // Pass empty object as required parameter
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    const plan = planQuery.data?.data;

    // Helper function to get school name with fallback
    const getSchoolDisplayName = (schoolPlan: any) => {
        if (schoolPlan.schoolName) return schoolPlan.schoolName;
        if (schoolPlan.school?.name) return schoolPlan.school.name;
        if (schoolPlan.name) return schoolPlan.name;
        if (schoolsQuery.data?.data && schoolPlan.schoolId) {
            const school = schoolsQuery.data.data.find((s: any) => s.id === schoolPlan.schoolId);
            if (school) return school.name;
        }
        return `School ID: ${schoolPlan.schoolId || schoolPlan.id}`;
    };

    const getSchoolDisplayLocation = (schoolPlan: any) => {
        if (schoolPlan.schoolLocation) return schoolPlan.schoolLocation;
        if (schoolPlan.school?.location) return schoolPlan.school.location;
        if (schoolPlan.location) return schoolPlan.location;
        if (schoolsQuery.data?.data && schoolPlan.schoolId) {
            const school = schoolsQuery.data.data.find((s: any) => s.id === schoolPlan.schoolId);
            if (school) return school.location;
        }
        return null;
    };

    const openEditModal = () => {
        setEditPlanModal({ open: true });
    };

    const closeEditModal = () => {
        setEditPlanModal({ open: false });
    };

    const handlePlanUpdated = () => {
        planQuery.refetch();
        closeEditModal();
        addToast({
            type: 'success',
            text: 'Plan updated successfully'
        });
    };

    const handleAddedToPlan = () => {
        planFeaturesQuery.refetch();
        planSchoolsQuery.refetch();
        setAddToPlanModal({ open: false });
    };

    const handleRemoveFeature = (featureId: string) => {
        openAlertConfirm({
            title: 'Remove Feature',
            message: 'Are you sure you want to remove this feature from the plan?',
            onConfirmed: async () => {
                try {
                    await plansService.deletePlanFeature(featureId);
                    planFeaturesQuery.refetch();
                    addToast({
                        type: 'success',
                        text: 'Feature removed from plan successfully'
                    });
                } catch (error: any) {
                    addToast({
                        type: 'error',
                        text: error.message || 'Failed to remove feature'
                    });
                }
            }
        });
    };

    const handleRemoveSchool = (schoolPlanId: string) => {
        openAlertConfirm({
            title: 'Remove School',
            message: 'Are you sure you want to remove this school from the plan?',
            onConfirmed: async () => {
                try {
                    await plansService.deleteSchoolPlan(schoolPlanId);
                    planSchoolsQuery.refetch();
                    addToast({
                        type: 'success',
                        text: 'School removed from plan successfully'
                    });
                } catch (error: any) {
                    addToast({
                        type: 'error',
                        text: error.message || 'Failed to remove school'
                    });
                }
            }
        });
    };

    const deletePlanMutation = useMutation({
        mutationFn: () => plansService.deletePlan(planId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['plans'] });
            addToast({
                type: 'success',
                text: 'Plan deleted successfully'
            });
            navigate('/payments/subscriptions/plans');
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                text: error.message || 'Failed to delete plan'
            });
        }
    });

    const handleDeletePlan = () => {
        if (!plan) return;
        openAlertConfirm({
            title: 'Delete Plan',
            message: `Are you sure you want to delete "${plan.name}"? This action cannot be undone.`,
            onConfirmed: () => {
                deletePlanMutation.mutate();
            }
        });
    };

    // Client-side search + pagination for features
    const [featureSearch, setFeatureSearch] = useState('');
    const [featurePage, setFeaturePage] = useState(1);
    const [featurePageSize, setFeaturePageSize] = useState(10);
    const filteredFeatures = useMemo(() => {
        const list: any[] = planFeaturesQuery.data?.data || [];
        const q = featureSearch.trim().toLowerCase();
        const filtered = q ? list.filter((f: any) => (
            (f.displayName || '').toLowerCase().includes(q) ||
            (f.systemCode || '').toLowerCase().includes(q) ||
            (f.description || '').toLowerCase().includes(q)
        )) : list;
        return filtered;
    }, [planFeaturesQuery.data, featureSearch]);
    const paginatedFeatures = useMemo(() => {
        const start = (featurePage - 1) * featurePageSize;
        return filteredFeatures.slice(start, start + featurePageSize);
    }, [filteredFeatures, featurePage, featurePageSize]);

    // Selection and bulk delete for features
    const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
    const [deletingFeatures, setDeletingFeatures] = useState(false);
    const isAllFeaturesOnPageSelected = paginatedFeatures.length > 0 && paginatedFeatures.every((f: any) => selectedFeatureIds.includes(f.id));
    const toggleSelectAllFeaturesOnPage = () => {
        setSelectedFeatureIds(prev => {
            if (isAllFeaturesOnPageSelected) {
                const idsOnPage = paginatedFeatures.map((f: any) => f.id);
                return prev.filter(id => !idsOnPage.includes(id));
            } else {
                const idsOnPage = paginatedFeatures.map((f: any) => f.id);
                return Array.from(new Set([...prev, ...idsOnPage]));
            }
        });
    };
    const toggleFeatureSelected = (id: string) => {
        setSelectedFeatureIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    useEffect(() => { setSelectedFeatureIds([]); }, [featurePage, featurePageSize, featureSearch]);

    const handleBulkDeleteFeatures = () => {
        if (selectedFeatureIds.length === 0) return;
        openAlertConfirm({
            title: 'Remove Selected Features',
            message: `Are you sure you want to remove ${selectedFeatureIds.length} feature(s) from the plan?`,
            onConfirmed: async () => {
                setDeletingFeatures(true);
                try {
                    await Promise.all(selectedFeatureIds.map((id) => plansService.deletePlanFeature(id)));
                    setSelectedFeatureIds([]);
                    planFeaturesQuery.refetch();
                    addToast({ type: 'success', text: 'Selected feature(s) removed' });
                } catch (e: any) {
                    addToast({ type: 'error', text: e?.message || 'Failed to remove some features' });
                } finally {
                    setDeletingFeatures(false);
                }
            }
        });
    };

    // Client-side search + pagination for schools
    const [schoolSearch, setSchoolSearch] = useState('');
    const [schoolPage, setSchoolPage] = useState(1);
    const [schoolPageSize, setSchoolPageSize] = useState(10);
    const filteredSchools = useMemo(() => {
        const list: any[] = planSchoolsQuery.data?.data || [];
        const q = schoolSearch.trim().toLowerCase();
        const filtered = q ? list.filter((sp: any) => {
            const name = getSchoolDisplayName(sp).toLowerCase();
            const location = (getSchoolDisplayLocation(sp) || '').toLowerCase();
            return name.includes(q) || location.includes(q);
        }) : list;
        return filtered;
    }, [planSchoolsQuery.data, schoolSearch]);
    const paginatedSchools = useMemo(() => {
        const start = (schoolPage - 1) * schoolPageSize;
        return filteredSchools.slice(start, start + schoolPageSize);
    }, [filteredSchools, schoolPage, schoolPageSize]);

    // Selection and bulk delete for schools
    const [selectedSchoolPlanIds, setSelectedSchoolPlanIds] = useState<string[]>([]);
    const [deletingSchools, setDeletingSchools] = useState(false);
    const isAllSchoolsOnPageSelected = paginatedSchools.length > 0 && paginatedSchools.every((sp: any) => selectedSchoolPlanIds.includes(sp.id));
    const toggleSelectAllSchoolsOnPage = () => {
        setSelectedSchoolPlanIds(prev => {
            if (isAllSchoolsOnPageSelected) {
                const idsOnPage = paginatedSchools.map((sp: any) => sp.id);
                return prev.filter(id => !idsOnPage.includes(id));
            } else {
                const idsOnPage = paginatedSchools.map((sp: any) => sp.id);
                return Array.from(new Set([...prev, ...idsOnPage]));
            }
        });
    };
    const toggleSchoolSelected = (id: string) => {
        setSelectedSchoolPlanIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    useEffect(() => { setSelectedSchoolPlanIds([]); }, [schoolPage, schoolPageSize, schoolSearch]);

    const handleBulkDeleteSchools = () => {
        if (selectedSchoolPlanIds.length === 0) return;
        openAlertConfirm({
            title: 'Remove Selected Schools',
            message: `Are you sure you want to remove ${selectedSchoolPlanIds.length} school(s) from the plan?`,
            onConfirmed: async () => {
                setDeletingSchools(true);
                try {
                    await Promise.all(selectedSchoolPlanIds.map((id) => plansService.deleteSchoolPlan(id)));
                    setSelectedSchoolPlanIds([]);
                    planSchoolsQuery.refetch();
                    addToast({ type: 'success', text: 'Selected school(s) removed' });
                } catch (e: any) {
                    addToast({ type: 'error', text: e?.message || 'Failed to remove some schools' });
                } finally {
                    setDeletingSchools(false);
                }
            }
        });
    };

    // Reset to first page on search or page size change
    useEffect(() => { setFeaturePage(1); }, [featureSearch, featurePageSize]);
    useEffect(() => { setSchoolPage(1); }, [schoolSearch, schoolPageSize]);

    

    if (planQuery.data?.notFound) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <i className="ri-file-search-line text-6xl text-gray-400 mb-4"></i>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Plan Not Found
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                        The plan you're looking for doesn't exist or has been removed.
                    </p>
                    <Button onClick={() => navigate('/payments/subscriptions/plans')}>
                        Back to Plans
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <ListState
                isLoading={planQuery.isLoading}
                isError={planQuery.isError}
                listCount={plan ? 1 : 0}
                totalDataCount={plan ? 1 : 0}
                hasSearch={false}
                onTryAgain={() => planQuery.refetch()}
            />

            {plan && (
                <div className='bg-white dark:bg-gray-800  dark:shadow-none rounded-xl divide-y dark:divide-gray-700 py-8 px-8'>
                    <div className='flex justify-between pb-6'>
                        <div className='flex items-center mr-5'>
                            <button
                                onClick={() => navigate('/payments/subscriptions/plans')}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mr-3"
                            >
                                <i className="ri-arrow-left-line text-xl"></i>
                            </button>
                            <div>
                                <h2 className='text-2xl font-medium mb-1'>
                                    {plan.name}
                                    {plan.defaultPlan && (
                                        <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">
                                            Default
                                        </span>
                                    )}
                                </h2>
                                <p className='text-gray-500 dark:text-gray-400'>{plan.description}</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Button onClick={openEditModal}>
                                Edit Plan
                            </Button>
                            <Button 
                                onClick={handleDeletePlan}
                                className="!text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:!text-red-400"
                            >
                                Delete Plan
                            </Button>
                            <Button onClick={() => setAddToPlanModal({ open: true })}>
                                Add Schools/Features
                            </Button>
                        </div>
                    </div>

                    <div className='pt-6 pb-6'>
                        <h2 className='font-medium text-xl mb-2'>Plan Details</h2>

                        <div className='grid sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-8'>
                            <div>
                                <div className='py-3.5'>
                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Status</p>
                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                        plan.active 
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                    }`}>
                                        {plan.active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className='py-3.5'>
                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Price per Student</p>
                                    <p>{plan.currency} {plan.pricePerStudent.toFixed(2)}</p>
                                </div>
                            </div>

                            <div>
                                <div className='py-3.5'>
                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Currency</p>
                                    <p>{plan.currency}</p>
                                </div>
                                <div className='py-3.5'>
                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Created At</p>
                                    <p>{format(new Date(plan.createdAt), 'do LLLL yyyy h:mm a')}</p>
                                </div>
                            </div>

                            <div>
                                <div className='py-3.5'>
                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Last Updated</p>
                                    <p>{format(new Date(plan.updatedAt), 'do LLLL yyyy h:mm a')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Plan Features Section */}
                    <div className='pt-6 pb-6 border-t dark:border-gray-700'>
                        <div className='flex justify-between items-center mb-3'>
                            <h2 className='font-medium text-xl'>Plan Features</h2>
                            <Button onClick={() => setAddToPlanModal({ open: true })} className="text-sm">
                                <i className="ri-add-line mr-1"></i>
                                Add Feature
                            </Button>
                        </div>

                        {/* Controls */}
                        <div className='flex flex-wrap items-center gap-3 mb-4'>
                            <Input
                                type='text'
                                value={featureSearch}
                                onChange={(e) => setFeatureSearch(e.target.value)}
                                placeholder='Search features by name, code, or description'
                                className='sm:w-full sm:max-w-xs md:max-w-sm'
                            />
                            <div className='ml-auto flex items-center gap-2'>
                                <span className='text-sm text-gray-500'>Show</span>
                                <select
                                    value={featurePageSize}
                                    onChange={(e) => setFeaturePageSize(parseInt(e.target.value, 10))}
                                    className='border rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-700'
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span className='text-sm text-gray-500'>per page</span>
                            </div>
                        </div>

                        {planFeaturesQuery.isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : filteredFeatures.length > 0 ? (
                            <div className="grid gap-3">
                                {/* Bulk actions bar */}
                                {selectedFeatureIds.length > 0 && (
                                    <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                        <p className="text-sm text-blue-800 dark:text-blue-300">{selectedFeatureIds.length} selected</p>
                                        <Button type='button' onClick={handleBulkDeleteFeatures} loading={deletingFeatures} className='!text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:!text-red-400'>Delete Selected</Button>
                                    </div>
                                )}
                                {paginatedFeatures.map((feature: any) => (
                                    <div key={feature.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <div className="flex items-center gap-3 flex-1">
                                            <div className="w-6 flex justify-center items-center">
                                                <CustomCheckbox
                                                    checked={selectedFeatureIds.includes(feature.id)}
                                                    onChange={() => toggleFeatureSelected(feature.id)}
                                                    ariaLabel="Select feature"
                                                />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-gray-900 dark:text-white">
                                                    {feature.displayName}
                                                </h3>
                                                {feature.description && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {feature.description}
                                                    </p>
                                                )}
                                                {feature.systemCode && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                        Code: {feature.systemCode}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveFeature(feature.id)}
                                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2"
                                            title="Remove feature"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                ))}
                                {/* Select all on page */}
                                {paginatedFeatures.length > 0 && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <CustomCheckbox checked={isAllFeaturesOnPageSelected} onChange={toggleSelectAllFeaturesOnPage} ariaLabel="Select all features on page" />
                                        <span className="text-sm text-gray-600">Select all on page</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <i className="ri-settings-line text-4xl mb-2 block"></i>
                                <p>No features found.</p>
                            </div>
                        )}

                        {/* Pagination */}
                        {filteredFeatures.length > 0 && (
                            <div className='flex justify-between items-center flex-wrap mt-4'>
                                <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>
                                    Showing {(featurePage - 1) * featurePageSize + 1}-{Math.min(featurePage * featurePageSize, filteredFeatures.length)} of {filteredFeatures.length}
                                </p>
                                <Pagination
                                    currentPage={featurePage}
                                    pageSize={featurePageSize}
                                    totalCount={filteredFeatures.length}
                                    onPageChange={(page) => setFeaturePage(page)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Plan Schools Section */}
                    <div className='pt-6 border-t dark:border-gray-700'>
                        <div className='flex justify-between items-center mb-3'>
                            <h2 className='font-medium text-xl'>
                                {(planSchoolsQuery.data?.data?.length || 0)} Schools on this Plan
                            </h2>
                            <Button onClick={() => setAddToPlanModal({ open: true })} className="text-sm">
                                <i className="ri-add-line mr-1"></i>
                                Add School
                            </Button>
                        </div>

                        {/* Controls */}
                        <div className='flex flex-wrap items-center gap-3 mb-4'>
                            <Input
                                type='text'
                                value={schoolSearch}
                                onChange={(e) => setSchoolSearch(e.target.value)}
                                placeholder='Search schools by name or location'
                                className='sm:w-full sm:max-w-xs md:max-w-sm'
                            />
                            <div className='ml-auto flex items-center gap-2'>
                                <span className='text-sm text-gray-500'>Show</span>
                                <select
                                    value={schoolPageSize}
                                    onChange={(e) => setSchoolPageSize(parseInt(e.target.value, 10))}
                                    className='border rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-700'
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span className='text-sm text-gray-500'>per page</span>
                            </div>
                        </div>

                        {planSchoolsQuery.isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : filteredSchools.length > 0 ? (
                            <div className="grid gap-3">
                                {/* Bulk actions bar */}
                                {selectedSchoolPlanIds.length > 0 && (
                                    <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                        <p className="text-sm text-blue-800 dark:text-blue-300">{selectedSchoolPlanIds.length} selected</p>
                                        <Button type='button' onClick={handleBulkDeleteSchools} loading={deletingSchools} className='!text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:!text-red-400'>Delete Selected</Button>
                                    </div>
                                )}
                                {paginatedSchools.map((schoolPlan: any) => (
                                    <div key={schoolPlan.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <div className="flex items-center gap-3 flex-1">
                                            <div className="w-6 flex justify-center items-center">
                                                <CustomCheckbox
                                                    checked={selectedSchoolPlanIds.includes(schoolPlan.id)}
                                                    onChange={() => toggleSchoolSelected(schoolPlan.id)}
                                                    ariaLabel="Select school"
                                                />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-gray-900 dark:text-white">
                                                    {getSchoolDisplayName(schoolPlan)}
                                                </h3>
                                                {getSchoolDisplayLocation(schoolPlan) && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {getSchoolDisplayLocation(schoolPlan)}
                                                    </p>
                                                )}
                                                {schoolPlan.createdAt && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                        Added: {format(new Date(schoolPlan.createdAt), 'do MMM yyyy')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveSchool(schoolPlan.id)}
                                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2"
                                            title="Remove school"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                ))}
                                {/* Select all on page */}
                                {paginatedSchools.length > 0 && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <CustomCheckbox checked={isAllSchoolsOnPageSelected} onChange={toggleSelectAllSchoolsOnPage} ariaLabel="Select all schools on page" />
                                        <span className="text-sm text-gray-600">Select all on page</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <i className="ri-school-line text-4xl mb-2 block"></i>
                                <p>No schools found.</p>
                            </div>
                        )}

                        {/* Pagination */}
                        {filteredSchools.length > 0 && (
                            <div className='flex justify-between items-center flex-wrap mt-4'>
                                <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>
                                    Showing {(schoolPage - 1) * schoolPageSize + 1}-{Math.min(schoolPage * schoolPageSize, filteredSchools.length)} of {filteredSchools.length}
                                </p>
                                <Pagination
                                    currentPage={schoolPage}
                                    pageSize={schoolPageSize}
                                    totalCount={filteredSchools.length}
                                    onPageChange={(page) => setSchoolPage(page)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {plan && (
                <ModalCreatePlan
                    open={editPlanModal.open}
                    plan={plan}
                    onClose={closeEditModal}
                    onSuccess={handlePlanUpdated}
                />
            )}

            {planId && (
                <ModalAddToPlan
                    open={addToPlanModal.open}
                    planId={planId}
                    onClose={() => setAddToPlanModal({ open: false })}
                    onSuccess={handleAddedToPlan}
                />
            )}
        </div>
    );
}

export default PlanDetailsPage;